import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { Table } from "react-bootstrap";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import { numberFormat } from "../../utils/numberFormat";

const DayWiseReportLikeFormat = () => {
    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    const [expandedMonths, setExpandedMonths] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [process, setProcess] = useState([]);

    // ✅ date helpers
    const fmtDate = (d) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const getLast7 = () => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - 6);
        return { start: fmtDate(start), end: fmtDate(end) };
    };

    const init7 = getLast7();

    const [fromDate, setFromDate] = useState(() => init7.start);
    const [toDate, setToDate] = useState(() => init7.end);
    const [appliedDate, setAppliedDate] = useState({ from: init7.start, to: init7.end });

    // ✅ dropdowns
    const [teamOpen, setTeamOpen] = useState(false);
    const [centerOpen, setCenterOpen] = useState(false);
    const [skillOpen, setSkillOpen] = useState(false);

    const [teamSearch, setTeamSearch] = useState("");
    const [centerSearch, setCenterSearch] = useState("");
    const [skillSearch, setSkillSearch] = useState("");

    const [selectedTeams, setSelectedTeams] = useState(new Set());
    const [appliedTeams, setAppliedTeams] = useState(new Set());

    const [selectedCenters, setSelectedCenters] = useState(new Set());
    const [appliedCenters, setAppliedCenters] = useState(new Set());

    const [selectedSkills, setSelectedSkills] = useState(new Set());
    const [appliedSkills, setAppliedSkills] = useState(new Set());

    const wrapRef = useRef(null);

    const toggleMonth = (mKey) => {
        setExpandedMonths((prev) => {
            const next = new Set(prev);
            if (next.has(mKey)) next.delete(mKey);
            else next.add(mKey);
            return next;
        });
    };

    // ✅ center map (suffix)
    const PERSON_BY_CODE = {
        MP: "REWA",
        DI: "DINESH",
        PU: "PUSHPENDAR",
        RI: "RICHA",
        MA: "MAHADESH",
        MY: "MYSORE CENTER",
        KRL: "KERALA",
        PL: "RAVI",
        TN: "MAHALAXMI",
        MU: "MURGESH",
        RCP: "RAHUL",
        G19: "KANTA",
        G10: "RAVI",
        SCP: "SHRINIWAS",
        CH: "RAVI",
        TRK: "TARAK",
        BA: "BANGLORE",
        SL: "SHAILENDRA",
        NOI: "ABHISHEK",
        CT: "CT",
        AM: "AMIT",
        KA: "KANIKA",
        SRK: "SHAHRUKH",
        JGT: "JAGAT",
        RNOI: "RAVI NOIDA",
        GNOI: "RAVI NOIDA",
        JS: "PUSHPENDAR",
        NI: "PUSHPENDAR",
        AN: "PUSHPENDAR",
        GZB: "GHAZIABAD",
        BL: "BALLARY",
        RV: "RAVINDRA",
        KO: "KOHAT NORTH",
        MN: "RAHUL",
        BU: "BURARI",
        D2C: "D2C",
        WA: "WHATSAPP",
        WH: "WHATSAPP",
        UN: "MAYANK",
        VP: "VIKAS PURI",
        SP: "SANJAY ASTRO",
        TG: "TG",
        NSP: "NSP",
        KOL: "KOLKATA",
        RI_MA: "MAHADESH",
        RCP_MN: "RAHUL MOTI NAGAR",
    };

    // ✅ team map (prefix)
    const TEAM_BY_CODE = {
        K: "Vivek Kumar",
        R: "Pragya Singh",
        J: "Akash Deep",
        V: "Pradeep Verma",
        T: "Yanshi",
        N: "Deepika Sisodia",
        D: "Deepak Kumar Giri",
        S: "Sumit Saini",
        I: "Ranjan Kumar",
        B: "Navin Chaurasia",
        E: "Bhumika Gola",
        G: "Vimal Jadon",
        P: "Anurodh Baral",
        M: "Simrati Kaur",
        Z: "Manprit Sir",
        A: "Ankita",
        W: "Ishika Pokhriyal",
        U: "Om Kumar",
        L: "Navin Chaurasia",
        Q: "Akash Deep",
    };

    // ✅ normalizers
    const normalizeSkill = (raw) => String(raw || "").trim().toUpperCase();

    const deriveTeamName = (skillRaw) => {
        const s = normalizeSkill(skillRaw);
        if (!s) return "Other";
        const parts = s.split("_").filter(Boolean);
        const first = (parts[0] || "").toUpperCase();
        return TEAM_BY_CODE[first] || "Other";
    };

    const deriveCenterName = (skillRaw) => {
        const s = normalizeSkill(skillRaw);
        if (!s) return "Other";
        const parts = s.split("_").filter(Boolean);
        const n = parts.length;
        const last = (parts[n - 1] || "").toUpperCase();
        const last2 = n >= 2 ? `${(parts[n - 2] || "").toUpperCase()}_${last}` : null;

        if (last2 && PERSON_BY_CODE[last2]) return PERSON_BY_CODE[last2];
        if (PERSON_BY_CODE[last]) return PERSON_BY_CODE[last];
        return "Other";
    };

    const uniqSorted = (arr) =>
        Array.from(new Set((arr || []).filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b)));

    const filterOptions = (opts, q) => {
        const s = String(q || "").trim().toLowerCase();
        if (!s) return opts;
        return opts.filter((x) => String(x).toLowerCase().includes(s));
    };

    // ✅ close dropdown on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) {
                setTeamOpen(false);
                setCenterOpen(false);
                setSkillOpen(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // ✅ API loader (ONLY date range)
    const loadReport = async (id, d1, d2) => {
        setIsLoading(true);
        try {
            const qs = new URLSearchParams();
            qs.set("start_date", d1 || "Okay");
            qs.set("end_date", d2 || "Okay");

            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-day-wise-call-report-with-proccess-name-by-id/${id}?${qs.toString()}`
            );

            if (res?.data?.status === 1) {
                const rows = Array.isArray(res?.data?.process) ? res.data.process : [];

                const normalized = rows.map((r) => ({
                    ...r,

                    // identity / filters
                    skillname: normalizeSkill(r?.skillname),
                    team_name: deriveTeamName(r?.skillname),
                    center_name: deriveCenterName(r?.skillname),

                    // totals (skill-level)
                    calls_unique_skill_server: Number(r?.calls_unique_skill_server) || 0,
                    calls_unique_exclusive: Number(r?.calls_unique_exclusive) || 0,
                    orders_count: Number(r?.orders_count) || 0,
                    value: Number(r?.value) || 0,
                    delivered_value: Number(r?.delivered_value) || 0,
                    delivered_count: Number(r?.delivered_count) || 0,
                    ppc_spend_total: Number(r?.ppc_spend_total) || 0,
                    tkt_size: Number(r?.tkt_size) || 0,
                    del_pct: Number(r?.del_pct) || 0,

                    // ✅ daily maps (THIS IS THE FIX)
                    ppc_spend_daily: r?.ppc_spend_daily || {},

                    calls_unique_exclusive_daily: r?.calls_unique_exclusive_daily || {},
                    calls_unique_skill_server_daily: r?.calls_unique_skill_server_daily || {},

                    orders_count_daily: r?.orders_count_daily || {},
                    delivered_value_daily: r?.delivered_value_daily || {},
                    delivered_count_daily: r?.delivered_count_daily || {},

                    // if API gives it, we will use it for day-wise Volume
                    value_daily: r?.value_daily || {},
                }));

                setProcess(normalized);
            } else {
                toast.error(res?.data?.message || "Failed to load report");
                setProcess([]);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading report");
            setProcess([]);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ initial load: last 7 days
    useEffect(() => {
        if (!ppcid) return navigate("/");
        const d = getLast7();
        setAppliedDate({ from: d.start, to: d.end });
        loadReport(ppcid, d.start, d.end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    // ✅ dropdown options from data
    const teamOptions = useMemo(() => {
        const opts = uniqSorted(process.map((x) => x.team_name)).filter((x) => x !== "Other");
        return opts.length ? opts : ["Other"];
    }, [process]);

    const centerOptions = useMemo(() => {
        const opts = uniqSorted(process.map((x) => x.center_name)).filter((x) => x !== "Other");
        return opts;
    }, [process]);

    const skillOptions = useMemo(() => uniqSorted(process.map((x) => x.skillname)), [process]);

    // ✅ init selects
    useEffect(() => {
        setSelectedTeams(new Set());
        setAppliedTeams(new Set());
    }, [teamOptions]);

    useEffect(() => {
        setSelectedCenters(new Set());
        setAppliedCenters(new Set());
    }, [centerOptions]);

    useEffect(() => {
        setSelectedSkills(new Set());
        setAppliedSkills(new Set());
    }, [skillOptions]);

    const filteredTeamOptions = useMemo(() => filterOptions(teamOptions, teamSearch), [teamOptions, teamSearch]);
    const filteredCenterOptions = useMemo(() => filterOptions(centerOptions, centerSearch), [centerOptions, centerSearch]);
    const filteredSkillOptions = useMemo(() => filterOptions(skillOptions, skillSearch), [skillOptions, skillSearch]);

    const toggleSetItem = (setter, value) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });
    };

    const btnText = (appliedSet, allOptions) => {
        if (!appliedSet || appliedSet.size === 0) return "All";
        if (appliedSet.size === allOptions.length) return "All";
        if (appliedSet.size === 1) return Array.from(appliedSet)[0];
        return `${appliedSet.size} Selected`;
    };

    // ✅ UI filtering ONLY via dropdowns
    const filteredProcess = useMemo(() => {
        const tset = appliedTeams.size ? appliedTeams : new Set(teamOptions);
        const cset = appliedCenters.size ? appliedCenters : new Set(centerOptions);
        const sset = appliedSkills.size ? appliedSkills : new Set(skillOptions);

        return (process || []).filter((r) => tset.has(r.team_name) && cset.has(r.center_name) && sset.has(r.skillname));
    }, [process, appliedTeams, appliedCenters, appliedSkills, teamOptions, centerOptions, skillOptions]);

    // ✅ totals (grand)
    const totals = useMemo(() => {
        const n = (v) => Number(v) || 0;
        return filteredProcess.reduce(
            (acc, item) => {
                acc.calls_unique_skill_server += n(item.calls_unique_skill_server);
                acc.calls_unique_exclusive += n(item.calls_unique_exclusive);
                acc.orders_count += n(item.orders_count);
                acc.value += n(item.value);
                acc.ppc_spend_total += n(item.ppc_spend_total);
                acc.delivered_value += n(item.delivered_value);
                acc.delivered_count += n(item.delivered_count);
                acc.rows += 1;
                return acc;
            },
            {
                calls_unique_skill_server: 0,
                calls_unique_exclusive: 0,
                orders_count: 0,
                value: 0,
                ppc_spend_total: 0,
                delivered_value: 0,
                delivered_count: 0,
                rows: 0,
            }
        );
    }, [filteredProcess]);

    // ✅ KPIs (grand)
    const grandConvPct = totals.calls_unique_exclusive > 0 ? (totals.orders_count / totals.calls_unique_exclusive) * 100 : 0;
    const grandDelPct = totals.value > 0 ? (totals.delivered_value / totals.value) * 100 : 0;
    const grandTicket = totals.orders_count > 0 ? totals.value / totals.orders_count : 0;
    const grandTotalCost = totals.ppc_spend_total;
    const grandCallCost = totals.calls_unique_exclusive > 0 ? grandTotalCost / totals.calls_unique_exclusive : 0;
    const grandFactor = grandTotalCost > 0 ? totals.value / grandTotalCost : 0;

    const onApply = () => {
        const d1 = String(fromDate || "");
        const d2 = String(toDate || "");
        if (!d1 || !d2) return toast.error("Please select From Date and To Date");
        if (d1 > d2) return toast.error("From Date cannot be greater than To Date");

        setAppliedDate({ from: d1, to: d2 });

        setAppliedTeams(new Set(selectedTeams.size ? selectedTeams : []));
        setAppliedCenters(new Set(selectedCenters.size ? selectedCenters : []));
        setAppliedSkills(new Set(selectedSkills.size ? selectedSkills : []));

        setTeamOpen(false);
        setCenterOpen(false);
        setSkillOpen(false);

        loadReport(ppcid, d1, d2);
    };

    const onReset = () => {
        const d = getLast7();

        setFromDate(d.start);
        setToDate(d.end);
        setAppliedDate({ from: d.start, to: d.end });

        const tAll = new Set(teamOptions);
        const cAll = new Set(centerOptions);
        const sAll = new Set(skillOptions);

        setSelectedTeams(tAll);
        setAppliedTeams(tAll);

        setSelectedCenters(cAll);
        setAppliedCenters(cAll);

        setSelectedSkills(sAll);
        setAppliedSkills(sAll);

        setTeamSearch("");
        setCenterSearch("");
        setSkillSearch("");

        setTeamOpen(false);
        setCenterOpen(false);
        setSkillOpen(false);

        loadReport(ppcid, d.start, d.end);
    };

    // =========================
    // ✅ Month → Day rendering helpers
    // =========================
    const monthKey = (ymd) => {
        const [y, m] = String(ymd).split("-");
        return `${y}-${m}`;
    };

    const monthLabel = (ymKey) => {
        const [y, m] = ymKey.split("-");
        const dt = new Date(Number(y), Number(m) - 1, 1);
        return dt.toLocaleString("en-US", { month: "short", year: "numeric" });
    };

    const dayLabel = (ymd) => {
        const dt = new Date(ymd + "T00:00:00");
        const dow = dt.toLocaleString("en-US", { weekday: "long" });
        const dmy = dt.toLocaleString("en-US", { month: "long", day: "2-digit", year: "numeric" });
        return `${dow}, ${dmy}`;
    };

    const listDatesBetween = (from, to) => {
        const out = [];
        const a = new Date(from + "T00:00:00");
        const b = new Date(to + "T00:00:00");
        for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) out.push(fmtDate(d));
        return out;
    };

    const safeNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    // ✅ FIXED: build day-wise rows using ALL daily maps coming from API
    const dayWiseRows = useMemo(() => {
        const dates = listDatesBetween(appliedDate.from, appliedDate.to);

        const byDate = {};
        dates.forEach((d) => {
            byDate[d] = {
                date: d,
                calls_unique_skill_server: 0,
                calls_unique_exclusive: 0,
                orders_count: 0,
                value: 0,
                delivered_value: 0,
                delivered_count: 0,
                ppc_spend_total: 0,
            };
        });

        (filteredProcess || []).forEach((item) => {
            const spendDaily = item.ppc_spend_daily || {};
            const cExDaily = item.calls_unique_exclusive_daily || {};
            const cSkillDaily = item.calls_unique_skill_server_daily || {};
            const ordDaily = item.orders_count_daily || {};
            const delVDaily = item.delivered_value_daily || {};
            const delCDaily = item.delivered_count_daily || {};
            const valDaily = item.value_daily || {}; // optional

            dates.forEach((d) => {
                if (!byDate[d]) return;

                byDate[d].ppc_spend_total += safeNum(spendDaily[d]);
                byDate[d].calls_unique_exclusive += safeNum(cExDaily[d]);
                byDate[d].calls_unique_skill_server += safeNum(cSkillDaily[d]);
                byDate[d].orders_count += safeNum(ordDaily[d]);
                byDate[d].delivered_value += safeNum(delVDaily[d]);
                byDate[d].delivered_count += safeNum(delCDaily[d]);

                // ✅ Volume day-wise only if API sends value_daily
                byDate[d].value += safeNum(valDaily[d]);
            });
        });

        return dates.map((d) => byDate[d]);
    }, [filteredProcess, appliedDate.from, appliedDate.to]);

    const groupedByMonth = useMemo(() => {
        const map = new Map();

        dayWiseRows.forEach((r) => {
            const mk = monthKey(r.date);
            if (!map.has(mk)) {
                map.set(mk, {
                    monthKey: mk,
                    monthLabel: monthLabel(mk),
                    rows: [],
                    totals: {
                        calls_unique_skill_server: 0,
                        calls_unique_exclusive: 0,
                        orders_count: 0,
                        value: 0,
                        delivered_value: 0,
                        delivered_count: 0,
                        ppc_spend_total: 0,
                    },
                });
            }

            const g = map.get(mk);
            g.rows.push(r);

            g.totals.calls_unique_skill_server += safeNum(r.calls_unique_skill_server);
            g.totals.calls_unique_exclusive += safeNum(r.calls_unique_exclusive);
            g.totals.orders_count += safeNum(r.orders_count);
            g.totals.value += safeNum(r.value);
            g.totals.delivered_value += safeNum(r.delivered_value);
            g.totals.delivered_count += safeNum(r.delivered_count);
            g.totals.ppc_spend_total += safeNum(r.ppc_spend_total);
        });

        return Array.from(map.values()).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
    }, [dayWiseRows]);

    return (
        <div className="misDashboard" ref={wrapRef}>
            <Helmet>
                <title>SATKARTAR::PROCESS REPORT</title>
            </Helmet>

            <style>{`
        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}
        .dropWrap{position:relative; min-width:260px;}
        .dropBtn{
          width:260px;display:flex;align-items:center;justify-content:space-between;
          padding:6px 10px;border:1px solid rgba(0,0,0,.15);border-radius:6px;
          background:#fff;font-size:13px;
        }
        .dropMenu{
          position:absolute; top:100%; left:0; z-index:50; width:260px;
          background:#fff;border:1px solid rgba(0,0,0,.15);border-radius:10px;
          margin-top:6px;box-shadow:0 10px 30px rgba(0,0,0,.12);overflow:hidden;
        }
        .dropHead{padding:10px; border-bottom:1px solid rgba(0,0,0,.08);}
        .dropSearch{
          width:100%;border:1px solid rgba(0,0,0,.15);border-radius:8px;
          padding:7px 10px;font-size:13px;outline:none;
        }
        .dropActions{
          display:flex; padding:10px;border-bottom:1px solid rgba(0,0,0,.08);
          justify-content:space-between;
        }
        .dropList{max-height:240px; overflow:auto; padding:8px 5px;}
        .dropItem{display:flex; align-items:center; gap:10px; padding:6px 2px; font-size:13px;}
        .dropFoot{
          display:flex; gap:8px; justify-content:space-between; padding:10px;
          border-top:1px solid rgba(0,0,0,.08); background:#fafafa;
        }

        .caretBtn{
          width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;
          border:1px solid rgba(0,0,0,.2);border-radius:4px;background:#fff;cursor:pointer;
          font-size:12px;line-height:1;
        }
        .monthRow td{ background:#f3f6fb !important; font-weight:700; }
        .treeCell{display:flex;align-items:center;gap:8px;}
        .dayIndent{padding-left:26px;}
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>

                    <div className="col-12 col-lg-12 col-sm-6">
                        <div className="card hrpages">
                            <div className="card shadow-sm mt-2 mx-2">
                                <div className="card-body p-2">
                                    <div className="filterRow">
                                        <div className="filterBox">
                                            <label>From Date</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={fromDate}
                                                onChange={(e) => setFromDate(e.target.value)}
                                                disabled={isLoading}
                                            />
                                        </div>

                                        <div className="filterBox">
                                            <label>To Date</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={toDate}
                                                onChange={(e) => setToDate(e.target.value)}
                                                disabled={isLoading}
                                            />
                                        </div>

                                        {/* Team */}
                                        <div className="filterBox">
                                            <label>Team Name</label>
                                            <div className="dropWrap">
                                                <button
                                                    type="button"
                                                    className="dropBtn"
                                                    onClick={() => {
                                                        setTeamOpen((s) => !s);
                                                        setCenterOpen(false);
                                                        setSkillOpen(false);
                                                    }}
                                                    disabled={isLoading || !teamOptions.length}
                                                >
                                                    <span style={{ fontWeight: 600 }}>{btnText(appliedTeams, teamOptions)}</span>
                                                    <span style={{ opacity: 0.7 }}>▾</span>
                                                </button>

                                                {teamOpen && (
                                                    <div className="dropMenu">
                                                        <div className="dropHead">
                                                            <input
                                                                className="dropSearch"
                                                                value={teamSearch}
                                                                onChange={(e) => setTeamSearch(e.target.value)}
                                                                placeholder="Search team..."
                                                            />
                                                        </div>

                                                        <div className="dropActions">
                                                            <button type="button" className="btnselectall" onClick={() => setSelectedTeams(new Set(teamOptions))}>
                                                                Select All
                                                            </button>
                                                            <button type="button" className="btnresetall" onClick={() => setSelectedTeams(new Set())}>
                                                                Clear
                                                            </button>
                                                        </div>

                                                        <div className="dropList">
                                                            {filteredTeamOptions.map((name) => (
                                                                <label key={name} className="dropItem">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedTeams.has(name)}
                                                                        onChange={() => toggleSetItem(setSelectedTeams, name)}
                                                                    />
                                                                    <span>{name}</span>
                                                                </label>
                                                            ))}
                                                        </div>

                                                        <div className="dropFoot">
                                                            <button
                                                                type="button"
                                                                className="btnselectall"
                                                                onClick={() => {
                                                                    const all = new Set(teamOptions);
                                                                    setSelectedTeams(all);
                                                                    setAppliedTeams(all);
                                                                    setTeamSearch("");
                                                                    setTeamOpen(false);
                                                                }}
                                                            >
                                                                Reset All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => {
                                                                    const all = selectedTeams.size ? new Set(selectedTeams) : new Set(teamOptions);
                                                                    setAppliedTeams(all);
                                                                    setTeamOpen(false);
                                                                }}
                                                            >
                                                                Apply Teams
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Center */}
                                        <div className="filterBox">
                                            <label>Center Name</label>
                                            <div className="dropWrap">
                                                <button
                                                    type="button"
                                                    className="dropBtn"
                                                    onClick={() => {
                                                        setCenterOpen((s) => !s);
                                                        setTeamOpen(false);
                                                        setSkillOpen(false);
                                                    }}
                                                    disabled={isLoading || !centerOptions.length}
                                                >
                                                    <span style={{ fontWeight: 600 }}>{btnText(appliedCenters, centerOptions)}</span>
                                                    <span style={{ opacity: 0.7 }}>▾</span>
                                                </button>

                                                {centerOpen && (
                                                    <div className="dropMenu">
                                                        <div className="dropHead">
                                                            <input
                                                                className="dropSearch"
                                                                value={centerSearch}
                                                                onChange={(e) => setCenterSearch(e.target.value)}
                                                                placeholder="Search center..."
                                                            />
                                                        </div>

                                                        <div className="dropActions">
                                                            <button type="button" className="btnselectall" onClick={() => setSelectedCenters(new Set(centerOptions))}>
                                                                Select All
                                                            </button>
                                                            <button type="button" className="btnresetall" onClick={() => setSelectedCenters(new Set())}>
                                                                Clear
                                                            </button>
                                                        </div>

                                                        <div className="dropList">
                                                            {filteredCenterOptions.map((name) => (
                                                                <label key={name} className="dropItem">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedCenters.has(name)}
                                                                        onChange={() => toggleSetItem(setSelectedCenters, name)}
                                                                    />
                                                                    <span>{name}</span>
                                                                </label>
                                                            ))}
                                                        </div>

                                                        <div className="dropFoot">
                                                            <button
                                                                type="button"
                                                                className="btnselectall"
                                                                onClick={() => {
                                                                    const all = new Set(centerOptions);
                                                                    setSelectedCenters(all);
                                                                    setAppliedCenters(all);
                                                                    setCenterSearch("");
                                                                    setCenterOpen(false);
                                                                }}
                                                            >
                                                                Reset All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => {
                                                                    const all = selectedCenters.size ? new Set(selectedCenters) : new Set(centerOptions);
                                                                    setAppliedCenters(all);
                                                                    setCenterOpen(false);
                                                                }}
                                                            >
                                                                Apply Centers
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Skill */}
                                        <div className="filterBox">
                                            <label>Skill Name</label>
                                            <div className="dropWrap">
                                                <button
                                                    type="button"
                                                    className="dropBtn"
                                                    onClick={() => {
                                                        setSkillOpen((s) => !s);
                                                        setTeamOpen(false);
                                                        setCenterOpen(false);
                                                    }}
                                                    disabled={isLoading || !skillOptions.length}
                                                >
                                                    <span style={{ fontWeight: 600 }}>{btnText(appliedSkills, skillOptions)}</span>
                                                    <span style={{ opacity: 0.7 }}>▾</span>
                                                </button>

                                                {skillOpen && (
                                                    <div className="dropMenu">
                                                        <div className="dropHead">
                                                            <input
                                                                className="dropSearch"
                                                                value={skillSearch}
                                                                onChange={(e) => setSkillSearch(e.target.value)}
                                                                placeholder="Search skill..."
                                                            />
                                                        </div>

                                                        <div className="dropActions">
                                                            <button type="button" className="btnselectall" onClick={() => setSelectedSkills(new Set(skillOptions))}>
                                                                Select All
                                                            </button>
                                                            <button type="button" className="btnresetall" onClick={() => setSelectedSkills(new Set())}>
                                                                Clear
                                                            </button>
                                                        </div>

                                                        <div className="dropList">
                                                            {filteredSkillOptions.map((name) => (
                                                                <label key={name} className="dropItem">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedSkills.has(name)}
                                                                        onChange={() => toggleSetItem(setSelectedSkills, name)}
                                                                    />
                                                                    <span title={name}>{name}</span>
                                                                </label>
                                                            ))}
                                                        </div>

                                                        <div className="dropFoot">
                                                            <button
                                                                type="button"
                                                                className="btnselectall"
                                                                onClick={() => {
                                                                    const all = new Set(skillOptions);
                                                                    setSelectedSkills(all);
                                                                    setAppliedSkills(all);
                                                                    setSkillSearch("");
                                                                    setSkillOpen(false);
                                                                }}
                                                            >
                                                                Reset All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => {
                                                                    const all = selectedSkills.size ? new Set(selectedSkills) : new Set(skillOptions);
                                                                    setAppliedSkills(all);
                                                                    setSkillOpen(false);
                                                                }}
                                                            >
                                                                Apply Skills
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <button className="btnskillapply" onClick={onApply} disabled={isLoading}>
                                            Apply
                                        </button>

                                        <button className="btnresetall" onClick={onReset} disabled={isLoading}>
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* ✅ TABLE */}
                            <div className="table pagetable table-responsive mt-2">
                                <Table className="table-sm table-fixeds" responsive striped bordered hover>
                                    {/* ✅ Grand Total row */}
                                    <thead>
                                        <tr>
                                            <th colSpan={2} className="text-end" style={{ fontWeight: 700, fontSize: 14 }}>
                                                Grand Total
                                            </th>

                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{totals.calls_unique_skill_server}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{totals.calls_unique_exclusive}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{totals.orders_count}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(totals.value)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(grandTotalCost)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(totals.delivered_value)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{totals.delivered_count}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{grandConvPct.toFixed(2)}%</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(grandCallCost)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{grandDelPct.toFixed(2)}%</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(grandTicket)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{grandFactor.toFixed(2)}</th>
                                        </tr>
                                    </thead>

                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Skill / Day</th>
                                            <th>Call(Skill)</th>
                                            <th>Calls(All Skill)</th>
                                            <th>Count of Orders</th>
                                            <th>Volume</th>
                                            <th>Total Cost</th>
                                            <th>Delivered</th>
                                            <th>Del Count</th>
                                            <th>Conv(%)</th>
                                            <th>Call/Cost</th>
                                            <th>Del(%)</th>
                                            <th>Ticket Size</th>
                                            <th>Factor</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {isLoading ? (
                                            [...Array(10)].map((_, i) => (
                                                <tr key={i}>
                                                    {Array.from({ length: 14 }).map((__, idx) => (
                                                        <td key={idx}>
                                                            <Skeleton animation="wave" />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : groupedByMonth.length ? (
                                            groupedByMonth.map((m) => {
                                                const open = expandedMonths.has(m.monthKey);

                                                const convPct =
                                                    m.totals.calls_unique_exclusive > 0
                                                        ? (m.totals.orders_count / m.totals.calls_unique_exclusive) * 100
                                                        : 0;

                                                const delPct = m.totals.value > 0 ? (m.totals.delivered_value / m.totals.value) * 100 : 0;

                                                const ticket = m.totals.orders_count > 0 ? m.totals.value / m.totals.orders_count : 0;

                                                const callCost =
                                                    m.totals.calls_unique_exclusive > 0 ? m.totals.ppc_spend_total / m.totals.calls_unique_exclusive : 0;

                                                const factor = m.totals.ppc_spend_total > 0 ? m.totals.value / m.totals.ppc_spend_total : 0;

                                                return (
                                                    <React.Fragment key={m.monthKey}>
                                                        <tr className="monthRow">
                                                            <td></td>
                                                            <td style={{fontWeight:"600"}}>
                                                                <div className="treeCell">
                                                                    <button className="caretBtn" type="button" onClick={() => toggleMonth(m.monthKey)}>
                                                                        {open ? "−" : "+"}
                                                                    </button>
                                                                    <span>{m.monthLabel}</span>
                                                                </div>
                                                            </td>

                                                            <td style={{fontWeight:"600"}}>{m.totals.calls_unique_skill_server}</td>
                                                            <td style={{fontWeight:"600"}}>{m.totals.calls_unique_exclusive}</td>
                                                            <td style={{fontWeight:"600"}}>{m.totals.orders_count}</td>
                                                            <td style={{fontWeight:"600"}}>{numberFormat(m.totals.value)}</td>
                                                            <td style={{fontWeight:"600"}}>{numberFormat(m.totals.ppc_spend_total)}</td>
                                                            <td style={{fontWeight:"600"}}>{numberFormat(m.totals.delivered_value)}</td>
                                                            <td style={{fontWeight:"600"}}>{m.totals.delivered_count}</td>
                                                            <td style={{fontWeight:"600"}}>{convPct.toFixed(2)}%</td>
                                                            <td style={{fontWeight:"600"}}>{numberFormat(callCost)}</td>
                                                            <td style={{fontWeight:"600"}}>{delPct.toFixed(2)}%</td>
                                                            <td style={{fontWeight:"600"}}>{numberFormat(ticket)}</td>
                                                            <td style={{fontWeight:"600"}}>{factor.toFixed(2)}</td>
                                                        </tr>

                                                        {open &&
                                                            m.rows.map((dRow, idx) => {
                                                                const dConv = dRow.calls_unique_exclusive > 0 ? (dRow.orders_count / dRow.calls_unique_exclusive) * 100 : 0;
                                                                const dDel = dRow.value > 0 ? (dRow.delivered_value / dRow.value) * 100 : 0;
                                                                const dTicket = dRow.orders_count > 0 ? dRow.value / dRow.orders_count : 0;
                                                                const dCallCost = dRow.calls_unique_exclusive > 0 ? dRow.ppc_spend_total / dRow.calls_unique_exclusive : 0;
                                                                const dFactor = dRow.ppc_spend_total > 0 ? dRow.value / dRow.ppc_spend_total : 0;

                                                                return (
                                                                    <tr key={dRow.date}>
                                                                        <td>{idx + 1}</td>
                                                                        <td className="dayIndent">{dayLabel(dRow.date)}</td>

                                                                        <td>{dRow.calls_unique_skill_server}</td>
                                                                        <td>{dRow.calls_unique_exclusive}</td>
                                                                        <td>{dRow.orders_count}</td>
                                                                        <td>{numberFormat(dRow.value)}</td>
                                                                        <td>{numberFormat(dRow.ppc_spend_total)}</td>
                                                                        <td>{numberFormat(dRow.delivered_value)}</td>
                                                                        <td>{dRow.delivered_count}</td>
                                                                        <td>{dConv.toFixed(2)}%</td>
                                                                        <td>{numberFormat(dCallCost)}</td>
                                                                        <td>{dDel.toFixed(2)}%</td>
                                                                        <td>{numberFormat(dTicket)}</td>
                                                                        <td>{dFactor.toFixed(2)}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                    </React.Fragment>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={14} className="text-center">
                                                    No Data
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="px-3 pb-2" style={{ fontSize: 12, opacity: 0.75 }}>
                                Showing: <b>{appliedDate.from}</b> to <b>{appliedDate.to}</b>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(DayWiseReportLikeFormat);
